<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const props = defineProps({
  entries: {
    type: Array,
    default: () => [],
  },
});

const darkMode = ref(false);
let colourSchemeQuery;

const updateColourScheme = (event) => {
  darkMode.value = event.matches;
};

onMounted(() => {
  colourSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
  darkMode.value = colourSchemeQuery.matches;
  colourSchemeQuery.addEventListener("change", updateColourScheme);
});

onBeforeUnmount(() => {
  colourSchemeQuery?.removeEventListener("change", updateColourScheme);
});

const chartData = computed(() => ({
  labels: props.entries.map((entry) => dayjs(entry.date).format("D MMM")),
  datasets: [
    {
      label: "Replies",
      data: props.entries.map((entry) => entry.replies_sent ?? 0),
      backgroundColor: "rgba(8, 145, 178, 0.75)",
      borderRadius: 4,
    },
    {
      label: "Tickets closed",
      data: props.entries.map((entry) => entry.tickets_closed ?? 0),
      backgroundColor: "rgba(16, 185, 129, 0.72)",
      borderRadius: 4,
    },
    {
      label: "Calls logged",
      data: props.entries.map((entry) => entry.calls_logged ?? 0),
      backgroundColor: "rgba(245, 158, 11, 0.72)",
      borderRadius: 4,
    },
    {
      label: "Quotes sent",
      data: props.entries.map((entry) => entry.quotes_sent ?? 0),
      backgroundColor: "rgba(99, 102, 241, 0.68)",
      borderRadius: 4,
    },
    {
      label: "Quotes accepted",
      data: props.entries.map((entry) => entry.quotes_accepted ?? 0),
      backgroundColor: "rgba(168, 85, 247, 0.7)",
      borderRadius: 4,
    },
    {
      label: "Invoices sent",
      data: props.entries.map((entry) => entry.invoices_sent ?? 0),
      backgroundColor: "rgba(14, 165, 233, 0.7)",
      borderRadius: 4,
    },
    {
      label: "Invoices marked paid",
      data: props.entries.map((entry) => entry.invoices_marked_paid ?? 0),
      backgroundColor: "rgba(20, 184, 166, 0.72)",
      borderRadius: 4,
    },
    {
      label: "Staff allocations",
      data: props.entries.map((entry) => entry.staff_allocations ?? 0),
      backgroundColor: "rgba(249, 115, 22, 0.7)",
      borderRadius: 4,
    },
    {
      label: "Vehicle allocations",
      data: props.entries.map((entry) => entry.vehicle_allocations ?? 0),
      backgroundColor: "rgba(124, 58, 237, 0.7)",
      borderRadius: 4,
    },
    {
      label: "Vehicle updates",
      data: props.entries.map((entry) => entry.vehicle_record_updates ?? 0),
      backgroundColor: "rgba(225, 29, 72, 0.68)",
      borderRadius: 4,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: {
        color: darkMode.value ? "#cbd5e1" : "#64748b",
        autoSkip: true,
        maxTicksLimit: 12,
        maxRotation: 0,
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: {
        color: darkMode.value
          ? "rgba(148, 163, 184, 0.18)"
          : "rgba(148, 163, 184, 0.2)",
        drawBorder: false,
      },
      ticks: {
        color: darkMode.value ? "#cbd5e1" : "#64748b",
        precision: 0,
      },
    },
  },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        color: darkMode.value ? "#cbd5e1" : "#64748b",
        usePointStyle: true,
        boxWidth: 8,
      },
    },
  },
}));
</script>

<template>
  <div class="h-72 w-full">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>
